import {
  GetRunEventsActionEnum,
  ListRunsStatusEnum,
  PaginatedRunEventsDTO,
  PaginatedRunsDTO,
  PlatformApi,
  PlatformDTO,
  PlatformSyncStatusDTO,
  ProductPageSyncApi,
  SyncRunResultDTO
} from "@app/api/generated";
import { getAuthorizedApiConfig } from "@app/util/api-config";

/** Thin wrappers around the generated ProductPageSyncApi, authorized like the rest of the admin. */

export const RUNS_PAGE_SIZE = 50;
export const EVENTS_PAGE_SIZE = 50;

interface IListRunsParams {
  page: number;
  platformId?: number;
  status?: ListRunsStatusEnum;
}

interface IListEventsParams {
  runId: number;
  page: number;
  action?: GetRunEventsActionEnum;
}

const getApi = async (): Promise<ProductPageSyncApi> => new ProductPageSyncApi(await getAuthorizedApiConfig());

/** Per-platform status for the sidebar (enabled flag, page counts, last run, isRunning). */
export const getPlatformStatuses = async (): Promise<PlatformSyncStatusDTO[]> => {
  const api = await getApi();
  return api.getPlatformStatuses();
};

/** A platform's full config, incl. the sync grace period + min-deal thresholds (run-detail tooltips). */
export const getPlatformById = async (platformId: number): Promise<PlatformDTO> => {
  const api = new PlatformApi(await getAuthorizedApiConfig());
  return api.getAllPlatforms({ platformId });
};

/** A page of sync runs (newest first), optionally filtered by platform and/or status. */
export const listRuns = async ({ page, platformId, status }: IListRunsParams): Promise<PaginatedRunsDTO> => {
  const api = await getApi();
  return api.listRuns({
    limit: RUNS_PAGE_SIZE,
    offset: (page - 1) * RUNS_PAGE_SIZE,
    platformId,
    status
  });
};

/** A single run by id, for the detail page. */
export const getRunById = async (runId: number): Promise<SyncRunResultDTO> => {
  const api = await getApi();
  return api.getRunById({ runId });
};

/** A page of a run's events, optionally filtered by action. */
export const getRunEvents = async ({ runId, page, action }: IListEventsParams): Promise<PaginatedRunEventsDTO> => {
  const api = await getApi();
  return api.getRunEvents({
    runId,
    limit: EVENTS_PAGE_SIZE,
    offset: (page - 1) * EVENTS_PAGE_SIZE,
    action
  });
};

/** Trigger a sync. Without a platformId the ESB runs every enabled platform. */
export const runSync = async (platformId?: number): Promise<SyncRunResultDTO[]> => {
  const api = await getApi();
  return api.runSync(platformId === undefined ? {} : { platformId });
};

/** Toggle a platform's product-page sync on/off; returns the refreshed status row. */
export const setPlatformEnabled = async (platformId: number, enabled: boolean): Promise<PlatformSyncStatusDTO> => {
  const api = await getApi();
  return api.setPlatformEnabled({ platformId, toggleProductPageSyncEnabledDTO: { enabled } });
};
